import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { IImageFile } from 'interfaces/files';

import { KEY_COMPETITORS_PRIORITY_OPTIONS } from 'components/BusinessToolForm/constants/options';

export const prefix =
  `${BUSINESS_TOOL_SECTIONS.MARKET_ANALYSIS}.${BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.COMPETITIVE_ANALYSIS}` as const;

export const STRENGTH_NAMES = {
  STRENGTH: 'strength',
} as const;

export interface IStrength {
  [STRENGTH_NAMES.STRENGTH]: string;
}

export const strengthValues = {
  [STRENGTH_NAMES.STRENGTH]: '',
};

export const WEAKNESSES_NAMES = {
  WEAKNESSES: 'weaknesses',
} as const;

export interface IWeaknesses {
  [WEAKNESSES_NAMES.WEAKNESSES]: string;
}

export const weaknessesValues = {
  [WEAKNESSES_NAMES.WEAKNESSES]: '',
};

export const COMPANY_QUALITIES_NAMES = {
  NAME: 'name',
  FILE: 'file',
  STRENGTH: 'strength',
  WEAKNESSES: 'weaknesses',
} as const;

export interface ICompanyQualities {
  [COMPANY_QUALITIES_NAMES.NAME]: string;
  [COMPANY_QUALITIES_NAMES.FILE]: IImageFile[];
  [COMPANY_QUALITIES_NAMES.STRENGTH]: IStrength[];
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: IWeaknesses[];
}

export const companyQualitiesValues: ICompanyQualities = {
  [COMPANY_QUALITIES_NAMES.NAME]: '',
  [COMPANY_QUALITIES_NAMES.FILE]: [],
  [COMPANY_QUALITIES_NAMES.STRENGTH]: [strengthValues],
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: [weaknessesValues],
};

export const KEY_COMPETITORS_NAMES = {
  FILE: 'file',
  COMPETITOR: 'competitor',
  WEBSITE: 'website',
  LOCATION: 'location',
  DESCRIPTION: 'description',
  POSITIONING: 'positioning',
  SALES_CHANNELS: 'salesChannels',
  PRICING_POLICY: 'pricingPolicy',
  ANNUAL_SALES_VOLUME: 'annualSalesVolume',
  MARKET_SHARE: 'marketShare',
  CUSTOMER_LOYALTY_LEVEL: 'customerLoyaltyLevel',
} as const;

export interface IKeyCompetitors {
  [KEY_COMPETITORS_NAMES.FILE]: IImageFile[];
  [KEY_COMPETITORS_NAMES.COMPETITOR]: string;
  [KEY_COMPETITORS_NAMES.WEBSITE]: string;
  [KEY_COMPETITORS_NAMES.LOCATION]: string;
  [KEY_COMPETITORS_NAMES.DESCRIPTION]: string;
  [KEY_COMPETITORS_NAMES.POSITIONING]: string;
  [KEY_COMPETITORS_NAMES.SALES_CHANNELS]: string;
  [KEY_COMPETITORS_NAMES.PRICING_POLICY]: string;
  [KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME]: string;
  [KEY_COMPETITORS_NAMES.MARKET_SHARE]: string;
  [KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL]: string;
}

export const keyCompetitorsValues: IKeyCompetitors = {
  [KEY_COMPETITORS_NAMES.FILE]: [],
  [KEY_COMPETITORS_NAMES.COMPETITOR]: '',
  [KEY_COMPETITORS_NAMES.WEBSITE]: '',
  [KEY_COMPETITORS_NAMES.LOCATION]: '',
  [KEY_COMPETITORS_NAMES.DESCRIPTION]: '',
  [KEY_COMPETITORS_NAMES.POSITIONING]: '',
  [KEY_COMPETITORS_NAMES.SALES_CHANNELS]: '',
  [KEY_COMPETITORS_NAMES.PRICING_POLICY]: '',
  [KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME]: '',
  [KEY_COMPETITORS_NAMES.MARKET_SHARE]: '',
  [KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL]: KEY_COMPETITORS_PRIORITY_OPTIONS[0].value,
};

export const FEATURES_FOR_COMPARISON_NAMES = {
  FEATURE: 'feature',
} as const;

export interface IFeaturesForComparison {
  [FEATURES_FOR_COMPARISON_NAMES.FEATURE]: string;
}

export const featuresForComparisonValues = {
  [FEATURES_FOR_COMPARISON_NAMES.FEATURE]: '',
};

export const FEATURES_LIST_NAMES = {
  FILE: 'file',
  NAME: 'name',
  FEATURE: 'feature',
} as const;

export interface IFeaturesList {
  [FEATURES_LIST_NAMES.FILE]: IImageFile[];
  [FEATURES_LIST_NAMES.NAME]: string;
}

export const featuresListValues = {
  [FEATURES_LIST_NAMES.FILE]: [],
  [FEATURES_LIST_NAMES.NAME]: '',
};

export const COMPETITORS_COMPARISON_NAMES = {
  NAME: 'name',
  PRODUCT: 'product',
  FEATURES_FOR_COMPARISON: 'featuresForComparison',
  FEATURES_LIST: 'featuresList',
  COMPETITOR_FEATURES_LIST: 'competitorFeaturesList',
} as const;

export interface ICompetitorsComparison {
  [COMPETITORS_COMPARISON_NAMES.PRODUCT]: string;
  [COMPETITORS_COMPARISON_NAMES.NAME]: string;
  [COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON]?: IFeaturesForComparison[];
  [COMPETITORS_COMPARISON_NAMES.FEATURES_LIST]: IFeaturesList[];
  [COMPETITORS_COMPARISON_NAMES.COMPETITOR_FEATURES_LIST]?: IFeaturesList[];
}

export const competitorsComparisonValues: ICompetitorsComparison = {
  [COMPETITORS_COMPARISON_NAMES.PRODUCT]: '',
  [COMPETITORS_COMPARISON_NAMES.NAME]: '',
  [COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON]: [],
  [COMPETITORS_COMPARISON_NAMES.FEATURES_LIST]: [featuresListValues],
  [COMPETITORS_COMPARISON_NAMES.COMPETITOR_FEATURES_LIST]: [featuresListValues],
};

export const COMPETITIVE_ANALYSIS_NAMES = {
  COMPETITORS: 'competitors',
  ATTRACTING_COMPETITORS_CUSTOMERS: 'attractingCompetitorsCustomers',
  COMPARISON_ITEM: 'comparisonItem',
  KEY_COMPETITORS: 'keyCompetitors',
  COMPETITORS_DIFFERENCE: 'competitorsDifference',
  YOUR_COMPANY_QUALITIES: 'yourCompanyQualities',
  COMPETITORS_QUALITIES: 'competitorsQualities',
  PURCHASING_PRODUCTS: 'purchasingProducts',
  FUTURE_EXPECTATIONS: 'futureExpectations',
  BARRIERS: 'barriers',
  CHANGING_OF_CURRENT_MARKET: 'changingOfCurrentMarket',
} as const;

export interface ICompetitiveAnalysis {
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS]: string;
  [COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS]: string;
  [COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM]: ICompetitorsComparison[];
  [COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS]: IKeyCompetitors[];
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES]: ICompanyQualities[];
  [COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES]: ICompanyQualities;
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE]: string;
  [COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS]: string;
  [COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS]: string;
  [COMPETITIVE_ANALYSIS_NAMES.BARRIERS]: string;
  [COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET]: string;
}

export const competitiveAnalysisInitialValues: ICompetitiveAnalysis = {
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS]: '',
  [COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS]: '',
  [COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM]: [competitorsComparisonValues],
  [COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS]: [keyCompetitorsValues],
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES]: [companyQualitiesValues],
  [COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES]: companyQualitiesValues,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE]: '',
  [COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS]: '',
  [COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS]: '',
  [COMPETITIVE_ANALYSIS_NAMES.BARRIERS]: '',
  [COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET]: '',
};

export const COMPETITIVE_ANALYSIS_MAX_LENGTHS = {
  FEATURE_LIST: {
    FEATURE_VALUE: 150,
  },
  COMPETITOR: {
    STRENGTH: 500,
    WEAKNESSES: 500,
  },
};
